import ResponseCodes from "../model/enum/response-codes";
import Pathnames from "../model/enum/pathnames";
import AlertService from "./AlertService";

export default class ApiService {
  // Base URL for the API
  private static baseUrl =
    process.env.NODE_ENV === "production"
      ? "https://supple-moon-377718.uc.r.appspot.com/api"
      : "http://localhost:5001/api";
  private static token = localStorage.getItem("accessToken");
  private static defaultTimeoutMs = 15000; // default request timeout of 15 seconds

  // Generic GET request
  static async get<T>(
    endpoint: string,
    token?: string,
    timeoutMs?: number,
  ): Promise<T> {
    const headers = {
      "Content-Type": "application/json",
      ...{ Authorization: `Bearer ${token ? token : this.token}` },
    };

    const controller = new AbortController();
    const signal = controller.signal;
    const timeout =
      typeof timeoutMs === "number" ? timeoutMs : this.defaultTimeoutMs;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/${endpoint}`, {
        method: "GET",
        headers,
        signal,
      });
    } catch (err) {
      if ((err as any)?.name === "AbortError") {
        throw new Error(`Request timed out after ${timeout}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (
      response.status === ResponseCodes.UNAUTHORIZED &&
      window.location.pathname.includes("admin")
    ) {
      AlertService.error("Session expired");
      localStorage.clear();
      setTimeout(() => {
        window.open(Pathnames.LOGIN, "_self");
      }, 2000);
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Generic POST request
  static async post<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    timeoutMs?: number,
  ): Promise<T> {
    const headers = {
      "Content-Type": "application/json",
      ...{ Authorization: `Bearer ${token ? token : this.token}` },
    };

    const controller = new AbortController();
    const signal = controller.signal;
    const timeout =
      typeof timeoutMs === "number" ? timeoutMs : this.defaultTimeoutMs;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(data),
        signal,
      });
    } catch (err) {
      if ((err as any)?.name === "AbortError") {
        throw new Error(`Request timed out after ${timeout}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (
      response.status === ResponseCodes.UNAUTHORIZED &&
      window.location.pathname.includes("admin")
    ) {
      AlertService.error("Session expired");
      localStorage.removeItem("accessToken");
      setTimeout(() => {
        window.open(Pathnames.LOGIN, "_self");
      }, 2000);
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Generic PUT request
  static async put<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    timeoutMs?: number,
  ): Promise<T> {
    const headers = {
      "Content-Type": "application/json",
      ...{ Authorization: `Bearer ${token ? token : this.token}` },
    };

    const controller = new AbortController();
    const signal = controller.signal;
    const timeout =
      typeof timeoutMs === "number" ? timeoutMs : this.defaultTimeoutMs;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/${endpoint}`, {
        method: "PUT",
        credentials: "include",
        headers,
        body: JSON.stringify(data),
        signal,
      });
    } catch (err) {
      if ((err as any)?.name === "AbortError") {
        throw new Error(`Request timed out after ${timeout}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (
      response.status === ResponseCodes.UNAUTHORIZED &&
      window.location.pathname.includes("admin")
    ) {
      AlertService.error("Session expired");
      localStorage.removeItem("accessToken");
      setTimeout(() => {
        window.open(Pathnames.LOGIN, "_self");
      }, 2000);
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Generic DELETE request
  static async delete<T>(
    endpoint: string,
    data?: unknown,
    token?: string,
    timeoutMs?: number,
  ): Promise<T> {
    const headers = {
      "Content-Type": "application/json",
      ...{ Authorization: `Bearer ${token ? token : this.token}` },
    };

    const controller = new AbortController();
    const signal = controller.signal;
    const timeout =
      typeof timeoutMs === "number" ? timeoutMs : this.defaultTimeoutMs;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/${endpoint}`, {
        method: "DELETE",
        credentials: "include",
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal,
      });
    } catch (err) {
      if ((err as any)?.name === "AbortError") {
        throw new Error(`Request timed out after ${timeout}ms`);
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (response.status === ResponseCodes.UNAUTHORIZED) {
      AlertService.error("Session expired");
      localStorage.removeItem("accessToken");
      setTimeout(() => {
        window.open(Pathnames.LOGIN, "_self");
      }, 2000);
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }
}
